import { handle } from "redux-pack";
import { EMessageButtonType } from "../../../components/general-ui/message-card/message-card";

import { Action } from "../../../types/actions";
import {
	IQuestionSocket,
	ISocketCommentProps,
	SqsSocketTypes,
	Status,
	BrandliveEvent,
} from "../../../types/working-model";
import {
	ADD_MODERATOR_CHAT_MESSAGE,
	UPDATE_MODERATOR_CHAT_MESSAGE,
	FETCH_MODERATOR_CHAT_MESSAGES,
	UNAPPROVE_MODERATOR_CHAT_MESSAGE,
	DELETED_MODERATOR_CHAT_MESSAGE,
	ADD_MODERATOR_QUESTION,
	ADD_MODERATOR_QUESTION_COMMENT,
	ADD_MODERATOR_QUESTION_LIKE,
	ADD_MODERATOR_QUESTION_UNLIKE,
	FETCH_MODERATOR_QUESTIONS_AND_COMMENTS,
	UPDATE_MODERATOR_QUESTION,
	GET_MODERATOR_VIDEO,
	UPDATE_MODERATOR_VIDEO,
	FETCH_MODERATOR_EVENTS_DATA,
	CLEAR_MODERATOR_STATE,
	MODERATOR_DELETE_QUESTION,
} from "../../actions/moderator/moderator";

export interface IModeratorState {
	activeEvent?: BrandliveEvent;
	fetchingActiveEvent: boolean;
	chat: Map<number, ISocketCommentProps>;
	loadingChat: boolean;
	fetchingMoreChat: boolean;
	hasMore: boolean;
	errorLoadingChat: string;
	lastFetchId: number | null;
	totalChatCount: number;
	deletedMessages: {
		messages: Map<string, any>;
		totalCount: number;
		loading: boolean;
		fetchingMore: boolean;
		hasMore: boolean;
		errorLoading: string;
		lastFetchId: number | null;
	};
	moderatorVideo: any;
	moderatorVideoIsLive: boolean;
	loadingModeratorVideo: boolean;
	noModeratorVideo: boolean;
	chatScrollCounter: number; // when this number changes, we know to show chat arrow message. The actual value does not matter.
	remeasureChatList: number; // when this number changes, we know to show chat arrow message. The actual value does not matter.
	questionsScrollCounter: number; // when this number changes, we know to show chat arrow message. The actual value does not matter.
	remeasureQuestionsList: number; // when this number changes, we know to show arrow message. The actual value does not matter.
	// questions_and_comments: Map<string, any>;
	questions_and_comments: Map<string, IQuestionSocket | ISocketCommentProps>;
	errorLoadingQuestions: string;
	loadingQuestions: boolean;
	questionsAndCommentsSize: number;
}

const initialState: IModeratorState = {
	activeEvent: undefined,
	fetchingActiveEvent: false,
	chat: new Map(),
	totalChatCount: 0,
	loadingChat: false,
	fetchingMoreChat: false,
	hasMore: false,
	errorLoadingChat: '',
	lastFetchId: null,
	deletedMessages: {
		messages: new Map(),
		totalCount: 0,
		loading: false,
		fetchingMore: false,
		hasMore: false,
		errorLoading: '',
		lastFetchId: null,
	},
	moderatorVideo: null,
	loadingModeratorVideo: false,
	moderatorVideoIsLive: true,
	noModeratorVideo: false,
	chatScrollCounter: 0,
	remeasureChatList: 0,
	questionsScrollCounter: 0,
	remeasureQuestionsList: 0,
	questions_and_comments: new Map(),
	errorLoadingQuestions: '',
	loadingQuestions: false,
	questionsAndCommentsSize: 0,
};

export default function ModeratorReducer(
	state: IModeratorState = initialState,
	action: Action
): IModeratorState {
	switch (action.type) {
		case FETCH_MODERATOR_CHAT_MESSAGES: {
			return handle(state, action, {
				start: state => ({ ...state, loadingChat: true, fetchingMoreChat: true }),
				failure: state => ({
					...state,
					errorLoadingChat: "Could not load messages."
				}),
				success: state => {
					// split the messages up between deleted and not deleted
					const updatedChat = new Map(state.chat);
					const updatedDeleted = new Map(state.deletedMessages.messages);

					action.payload.chat.forEach((msg: any) => {
						msg.message_type = SqsSocketTypes.CHAT;
						updatedChat.set(msg.id, msg);
						if (msg.status === Status.Deleted) {
							updatedDeleted.set(`chat-${msg.id}`, msg);
						}
					});

					return {
						...state,
						chat: updatedChat,
						hasMore: action.payload.has_more,
						errorLoadingChat: "",
						totalChatCount: updatedChat.size - updatedDeleted.size,
						lastFetchId: action?.payload?.chat[action?.payload?.chat?.length - 1]?.id || null,
						deletedMessages: {
							...state.deletedMessages,
							messages: updatedDeleted,
							totalCount: updatedDeleted.size,
						},
					};
				},
				finish: state => ({ ...state, loadingChat: false, fetchingMoreChat: false })
			});
		}
		case ADD_MODERATOR_CHAT_MESSAGE: {
			// create a copy of the map
			const mapCopy = new Map(state.chat);
			action.payload.message_type = SqsSocketTypes.CHAT;
			mapCopy.set(action.payload.id, action.payload);
			// set the latFetchId so we can properly paginate without getting duplicate from the database when
			// scroll fetching more items in the moderator panel
			const lastFetchId = state.lastFetchId || action?.payload?.id || null;
			return {
				...state,
				chat: mapCopy,
				totalChatCount: state.totalChatCount + 1,
				lastFetchId,
				chatScrollCounter: state.chatScrollCounter + 1,
				remeasureChatList: state.remeasureChatList + 1,
			};
		}
		case UPDATE_MODERATOR_CHAT_MESSAGE: {
			const updatedChat = new Map(state.chat);
			const updatedDeleted = new Map(state.deletedMessages.messages);
			const old = updatedChat.get(action.payload.id);
			if (!old) return state;

			const { actionType, ...restOfUpdate } = action.payload;
			let isUndeleted = false;
			let isDeleted = false;
			if (actionType && actionType === EMessageButtonType.UnDelete) {
				isUndeleted = true;
				updatedDeleted.delete(`chat-${action.payload.id}`);
			}
			if (actionType && actionType === EMessageButtonType.Delete) {
				isDeleted = true;
				updatedDeleted.set(`chat-${action.payload.id}`, restOfUpdate);
			}

			updatedChat.set(action.payload.id, { ...old, ...restOfUpdate });

			return {
				...state,
				chat: updatedChat,
				remeasureChatList: state.remeasureChatList + 1,
				totalChatCount: isUndeleted
					? state.totalChatCount + 1
					: isDeleted
						? state.totalChatCount - 1
						: state.totalChatCount,
				deletedMessages: {
					...state.deletedMessages,
					messages: updatedDeleted,
					totalCount: updatedDeleted.size,
				}
			};
		}
		case UNAPPROVE_MODERATOR_CHAT_MESSAGE: {
			const updated = new Map(state.chat);
			const msg = updated.get(action.payload);
			if (!msg) return state;

			const newMessage = { ...msg, status: Status.Unapproved, deleted_at: null };
			updated.set(newMessage.id, newMessage);

			// if message is in the deleted column, then remove it from there and move it back to the regular column
			const updatedDeleted = new Map(state.deletedMessages.messages);
			if (updatedDeleted.get(action.payload)) {
				updatedDeleted.delete(action.payload);
			}
			return {
				...state,
				chat: updated,
				deletedMessages: {
					...state.deletedMessages,
					messages: updatedDeleted,
				}
			};
		}
		case DELETED_MODERATOR_CHAT_MESSAGE: {
			const updated = new Map(state.chat);
			const old = updated.get(action.payload.id);
			if (!old) return state;
			updated.set(old.id, {
				...old,
				deleted_at: action.payload.deleted_at,
				status: Status.Deleted,
			});
			// do not delete from list, instead we will just not render the item if its status is deleted because
			// we want to be able to easily re-show the message in order if it's undeleted
			const updatedDeleted = new Map(state.deletedMessages.messages);
			updatedDeleted.set(action.payload.id, {
				...old,
				deleted_at: action.payload.deleted_at,
				status: Status.Deleted,
			});
			return {
				...state,
				chat: updated,
				totalChatCount: state.totalChatCount - 1,
				remeasureChatList: state.remeasureChatList + 1,
				deletedMessages: {
					...state.deletedMessages,
					messages: updatedDeleted,
					totalCount: state.deletedMessages.totalCount + 1,
				}
			};
		}
		case FETCH_MODERATOR_QUESTIONS_AND_COMMENTS: {
			return handle(state, action, {
				start: state => ({ ...state, loadingQuestions: true }),
				failure: state => ({
					...state,
					errorLoadingQuestions: "Could not load questions."
				}),
				success: state => {
					const questions_and_comments: Map<string, any> = new Map();
					const deletedMessagesCopy = new Map(state.deletedMessages.messages);
					const all: any[] = [];
					action.payload.forEach((qn: any) => {
						qn.message_type = SqsSocketTypes.QUESTION;
						const { comments, ...question } = qn;
						all.push(question);
						if (qn?.status === Status.Deleted) {
							deletedMessagesCopy.set(`question-${qn.id}`, question); // don't use qn, because we don't need all the comments in the list
						}
						questions_and_comments.set(`question-${qn.id}`, question); // don't use qn, because we don't need all the comments in the list
						// only display the comments if the question itself is not deleted
						if (comments?.[0]) {
							comments.forEach((comment: any) => {
								comment.original_question = question;
								comment.message_type = SqsSocketTypes.QUESTION_COMMENT;
								if (comment?.status === Status.Deleted) {
									deletedMessagesCopy.set(`question_comment-${comment.id}`, comment);
								}
								questions_and_comments.set(`question_comment-${comment.id}`, comment);
								all.push(comment);
							});
						}
					});

					const questionsAndCommentsSize = all.length;
					return {
						...state,
						questions_and_comments,
						questionsAndCommentsSize,
						deletedMessages: {
							...state.deletedMessages,
							messages: deletedMessagesCopy,
							totalCount: deletedMessagesCopy.size,
						}
					};
				},
				finish: state => ({ ...state, loadingQuestions: false })
			});
		}
		case ADD_MODERATOR_QUESTION: {
			const mapCopy = new Map(state.questions_and_comments);
			mapCopy.set(`question-${action.payload.id}`, action.payload);
			return {
				...state,
				questions_and_comments: mapCopy,
				questionsAndCommentsSize: state.questionsAndCommentsSize + 1,
				questionsScrollCounter: state.questionsScrollCounter + 1,
			};
		}
		case ADD_MODERATOR_QUESTION_COMMENT: {
			const mapCopy = new Map(state.questions_and_comments);
			const originalQn = state.questions_and_comments.get(`question-${action?.payload?.question_id}`);
			if (!originalQn) return state;
			action.payload.original_question = originalQn;
			mapCopy.set(`question_comment-${action.payload.id}`, action.payload);
			return {
				...state,
				questions_and_comments: mapCopy,
				questionsAndCommentsSize: state.questionsAndCommentsSize + 1,
				questionsScrollCounter: state.questionsScrollCounter + 1,
			};
		}
		case ADD_MODERATOR_QUESTION_LIKE: {
			return state;
		}
		case ADD_MODERATOR_QUESTION_UNLIKE: {
			return state;
		}
		case UPDATE_MODERATOR_QUESTION: {
			// find and update the question or question comment

			// because questions and question comments can potentially have the same id
			// we find the question with the "question-" prefix and comments with "question_comment-"
			const prefix = action.payload?.question_id ? 'question_comment-' : 'question-';

			// make a copy of questions_and_comments
			const updatedQuestionsAndComments = new Map(state.questions_and_comments);

			// make a copy of deleted
			const updatedDeleted = new Map(state.deletedMessages.messages);
			// extract payload type so we know what button was clicked
			const { actionType, ...restOfUpdate } = action.payload;

			const msg = updatedQuestionsAndComments.get(`${prefix}${action.payload.id}`);

			if (!msg) return state;

			updatedQuestionsAndComments.set(`${prefix}${msg.id}`, {
				...msg,
				...restOfUpdate,
			});

			let isUndeleted = false;
			let isDeleted = false;
			// determine what type of button was triggered.
			// If UnDelete, then delete the message from the deleted list
			if (actionType && actionType === EMessageButtonType.UnDelete) {
				isUndeleted = true;
				updatedDeleted.delete(`${prefix}${action.payload.id}`);
			}
			// if delete, then add the message to the deleted list
			if (actionType && actionType === EMessageButtonType.Delete) {
				isDeleted = true;
				updatedDeleted.set(`${prefix}${action.payload.id}`, restOfUpdate);
			}

			return {
				...state,
				questions_and_comments: updatedQuestionsAndComments,
				remeasureQuestionsList: state.remeasureQuestionsList + 1,
				questionsAndCommentsSize: isUndeleted
					? state.questionsAndCommentsSize + 1
					: isDeleted
						? state.questionsAndCommentsSize - 1
						: state.questionsAndCommentsSize,
				deletedMessages: {
					...state.deletedMessages,
					messages: updatedDeleted,
					totalCount: updatedDeleted.size,
				},
			};
		}
		case GET_MODERATOR_VIDEO: {
			return handle(state, action, {
				start: state => ({ ...state, loadingModeratorVideo: true }),
				finish: state => ({ ...state, loadingModeratorVideo: false }),
				success: state => {
					if (Object.keys(action.payload).length > 0) {
						return { ...state, moderatorVideo: action.payload, moderatorVideoIsLive: 'live_video' in action.payload };
					} else {
						return { ...state, noModeratorVideo: true, moderatorVideo: null };
					}
				}
			});
		}
		case UPDATE_MODERATOR_VIDEO: {
			return {
				...state,
				moderatorVideo: {
					live_video: {
						playbackUrl: action.payload
					}
				},
				noModeratorVideo: false,
				moderatorVideoIsLive: true,
				loadingModeratorVideo: false
			};
		}
		case FETCH_MODERATOR_EVENTS_DATA: {
			return handle(state, action, {
				start: state => ({ ...state, fetchingActiveEvent: true }),
				success: state => ({
					...state,
					activeEvent: action.payload,
				}),
				finish: state => ({ ...state, fetchingActiveEvent: false }),
			});
		}
		case CLEAR_MODERATOR_STATE: {
			// clear chat messages, video, questions and comments
			const copyOfInitialState = { ...initialState };
			// remove stuff we don't want to clear
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			const { activeEvent, fetchingActiveEvent, ...rest } = copyOfInitialState;
			return {
				...state,
				...rest,
			};
		}
		case MODERATOR_DELETE_QUESTION: {
			// get the comment
			// update the status to deleted
			// move to deleted map

			const { id, question_id } = action.payload;
			const prefix = question_id ? 'question_comment-' : 'question-';

			// make a copy of questions_and_comments
			const updatedQuestionsAndComments = new Map(state.questions_and_comments);
			// make a copy of deleted
			const updatedDeleted = new Map(state.deletedMessages.messages);

			const msg = updatedQuestionsAndComments.get(`${prefix}${id}`);

			if (!msg) return state;

			updatedQuestionsAndComments.set(`${prefix}${msg.id}`, {
				...msg,
				status: Status.Deleted,
			});

			updatedDeleted.set(`${prefix}${id}`, updatedQuestionsAndComments.get(`${prefix}${id}`));
			return {
				...state,
				questions_and_comments: updatedQuestionsAndComments,
				remeasureQuestionsList: state.remeasureQuestionsList + 1,
				questionsAndCommentsSize: state.questionsAndCommentsSize - 1,
				deletedMessages: {
					...state.deletedMessages,
					messages: updatedDeleted,
					totalCount: updatedDeleted.size,
				},
			};
		}
		default:
			return state;
	}
}
